import React from "react";
import { useHistory } from "react-router-dom";
import {
  CheckCircle2
} from "lucide-react";
import SEO from "../../components/SEO/SEO";
import "./index.css";

export default function EnterpriseReadyScreen() {
  const history = useHistory();

  return (
    <div className="enterprise-ready-container min-h-screen flex flex-col">
      <SEO 
        title="Why Organisations & Users Trust SPACIA - Platform" 
        description="Built on decades of enterprise software experience, verification, secure infrastructure and support designed to make booking with confidence simple."
      />

      {/* 1. HERO BANNER (Screenshot 1) */}
      <section className="pt-32 sm:pt-40 pb-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto w-full">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 space-y-6">
            <span className="text-blue-600 font-bold text-xs tracking-wider uppercase block">
              WHY TRUST SPACIA
            </span>
            <h1 className="text-4xl sm:text-6xl font-extrabold tracking-tight leading-tight text-gray-900">
              Why Organisations & <br /> Users Trust SPACIA
            </h1>
            <p className="text-gray-600 text-base sm:text-lg max-w-xl leading-relaxed">
              Built on decades of enterprise software experience, verification, secure infrastructure and support designed to make booking with confidence simple.
            </p>
            <div className="pt-2 flex flex-wrap gap-4">
              <button 
                onClick={() => history.push("/package-signup")}
                className="bg-blue-600 hover:bg-blue-700 text-white px-7 py-3.5 rounded-full font-bold text-sm shadow-lg transition-all flex items-center gap-2"
              >
                Get Started ➔
              </button>
              <button 
                onClick={() => history.push("/pricing")}
                className="bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 px-7 py-3.5 rounded-full font-bold text-sm transition-all"
              >
                Speak to Our Team
              </button>
            </div>
          </div>

          {/* Right Floating Booking Confirmation Card Mockup */}
          <div className="lg:col-span-5">
            <div className="bg-white border border-gray-200 rounded-3xl p-6 shadow-2xl space-y-6">
              <div className="bg-[#072654] text-white p-3 rounded-2xl flex justify-between items-center text-xs font-bold">
                <span>SPACIA</span>
                <span className="flex gap-1"><span className="w-1.5 h-1.5 rounded-full bg-white/60"></span><span className="w-1.5 h-1.5 rounded-full bg-white/60"></span></span>
              </div>

              <div className="space-y-2 border-b border-gray-100 pb-4">
                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block">BOOKING CONFIRMED</span>
                <div className="flex justify-between items-start">
                  <div>
                    <h4 className="font-bold text-base text-gray-900">Innovation Hub — Suite 3A</h4>
                    <p className="text-xs text-gray-400">📍 Accra, Ghana</p>
                  </div>
                  <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-bold text-xs">✓</span>
                </div>
                <div className="flex gap-2 pt-1">
                  <span className="bg-blue-50 text-blue-600 text-[10px] px-2.5 py-0.5 rounded-full font-bold">✓ Verified</span>
                  <span className="bg-emerald-50 text-emerald-600 text-[10px] px-2.5 py-0.5 rounded-full font-bold">✓ Booked</span>
                </div>
              </div>

              <div className="space-y-1.5 text-xs text-gray-600">
                <div className="flex justify-between"><span>DATE</span><span className="font-semibold text-gray-900">Monday, 14 July 2025</span></div>
                <div className="flex justify-between"><span>DURATION</span><span className="font-semibold text-gray-900">Full Day Pass</span></div>
                <div className="flex justify-between"><span>OCCUPANCY</span><span className="font-semibold text-gray-900">1 of 8 desks</span></div>
              </div>

              <div className="bg-blue-50/80 p-3 rounded-2xl border border-blue-100 flex items-center gap-3">
                <div className="w-7 h-7 rounded-full bg-blue-600 text-white font-bold text-xs flex items-center justify-center">🛡️</div>
                <div>
                  <p className="text-xs font-bold text-gray-900">Host Verified by SPACIA</p>
                  <p className="text-[10px] text-gray-500">Space and provider approved before listing</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. 5 TRUST PILLARS (Screenshot 1) */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto w-full border-t border-gray-100">
        <div className="grid grid-cols-1 sm:grid-cols-5 gap-4">
          {[
            { num: "01", title: "Built by theSOFTtribe", desc: "Three decades of enterprise software experience." },
            { num: "02", title: "Verified, Every Time", desc: "Every space, host and provider is approved before going live." },
            { num: "03", title: "Enterprise-Grade Security", desc: "Secure cloud infrastructure, encryption and access control." },
            { num: "04", title: "Built for Growth", desc: "Scales from individual bookings to multi-city enterprise." },
            { num: "05", title: "Local Support with Global Standards", desc: "Responsive local support backed by recognised practices." }
          ].map((item, idx) => (
            <div key={idx} className="bg-slate-50 border border-slate-200/80 rounded-2xl p-6 space-y-2 text-left hover:shadow-md transition-shadow">
              <span className="text-2xl font-extrabold text-blue-600 block">{item.num}</span>
              <h4 className="font-bold text-sm text-gray-900">{item.title}</h4>
              <p className="text-[11px] text-gray-500 leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* 3. THE FOUNDATION: BUILT BY THESOFTTRIBE (Screenshot 1) */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto w-full bg-slate-50 border-t border-b border-gray-200/80">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          {/* Left Timeline */}
          <div className="lg:col-span-5 space-y-4">
            <div className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm space-y-1">
              <span className="text-blue-600 font-bold text-xs">1991</span>
              <h4 className="font-bold text-sm text-gray-900">theSOFTtribe Founded</h4>
              <p className="text-[11px] text-gray-500">One of Africa's longest-established indigenous software companies.</p>
            </div>

            <div className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm space-y-2">
              <h4 className="font-bold text-xs text-gray-900">Three Decades of Enterprise Software</h4>
              <div className="flex flex-wrap gap-2 text-[10px] font-semibold text-gray-600">
                <span className="bg-slate-100 px-2.5 py-1 rounded-full">Governments</span>
                <span className="bg-slate-100 px-2.5 py-1 rounded-full">Educational Institutions</span>
                <span className="bg-slate-100 px-2.5 py-1 rounded-full">Private Sector</span>
              </div>
            </div>

            <div className="bg-[#072654] text-white rounded-2xl p-5 shadow-md">
              <h4 className="font-bold text-sm">SPACIA</h4>
              <p className="text-xs text-slate-300">That experience now powers SPACIA.</p>
            </div>
          </div>

          {/* Right Copy */}
          <div className="lg:col-span-7 space-y-6">
            <span className="text-blue-600 font-bold text-xs tracking-wider uppercase">THE FOUNDATION</span>
            <h2 className="text-3xl sm:text-5xl font-extrabold text-gray-900">Built by theSOFTtribe</h2>
            <p className="text-gray-600 text-sm sm:text-base leading-relaxed">
              Founded in 1991, theSOFTtribe is one of Africa's longest-established indigenous software companies. For more than three decades, we have designed, developed and supported enterprise software for governments, educational institutions and private sector organisations across Africa.
            </p>
            <p className="text-gray-600 text-sm sm:text-base leading-relaxed font-semibold">
              That experience now powers SPACIA.
            </p>
            <div className="bg-white border border-gray-200 rounded-2xl p-4 flex items-center gap-3 shadow-sm">
              <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 font-bold flex items-center justify-center text-lg">🏢</div>
              <div>
                <p className="text-xs font-bold text-gray-900">Founded in 1991</p>
                <p className="text-[10px] text-gray-500">Over three decades of African enterprise software</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 4. VERIFIED, EVERY TIME (Screenshot 2) */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto w-full">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 space-y-6">
            <span className="text-blue-600 font-bold text-xs tracking-wider uppercase">TRUST THROUGH VERIFICATION</span>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900">Verified, Every Time</h2>
            <p className="text-gray-600 text-sm sm:text-base leading-relaxed">
              Every workspace, property, host and provider on SPACIA is verified before it goes live so users can book with confidence.
            </p>
            <ul className="space-y-3 text-xs sm:text-sm font-medium text-gray-700">
              <li className="flex items-center gap-3"><CheckCircle2 className="w-4 h-4 text-blue-600" /> Every listing reviewed before going live</li>
              <li className="flex items-center gap-3"><CheckCircle2 className="w-4 h-4 text-blue-600" /> Hosts and providers are approved</li>
              <li className="flex items-center gap-3"><CheckCircle2 className="w-4 h-4 text-blue-600" /> Spaces are confirmed as described</li>
              <li className="flex items-center gap-3"><CheckCircle2 className="w-4 h-4 text-blue-600" /> Users book knowing what they'll find</li>
            </ul>
          </div>

          <div className="lg:col-span-6">
            {/* Verification Process Mockup */}
            <div className="bg-slate-50 border border-slate-200/80 rounded-3xl p-6 space-y-3">
              <div className="bg-white p-3 rounded-xl border border-gray-200 text-xs font-bold text-gray-700 flex justify-between items-center">
                <span>Space / Property / Host / Provider</span>
                <span className="text-[10px] text-gray-400 font-normal">Submits for listing</span>
              </div>
              <div className="text-center text-blue-600 font-bold text-xs">↓</div>
              <div className="bg-blue-600 text-white p-3 rounded-xl text-xs font-bold flex justify-between items-center">
                <span>Verification</span>
                <span className="text-[10px] text-blue-200">SPACIA reviews and approves</span>
              </div>
              <div className="text-center text-blue-600 font-bold text-xs">↓</div>
              <div className="bg-[#072654] text-white p-3 rounded-xl text-xs font-bold flex justify-between items-center">
                <span>Live</span>
                <span className="text-[10px] text-blue-200">Goes live on platform</span>
              </div>
              <div className="text-center text-blue-600 font-bold text-xs">↓</div>
              <div className="bg-emerald-50 text-emerald-700 p-3 rounded-xl border border-emerald-200 text-xs font-bold flex justify-between items-center">
                <span>User Booking</span>
                <span className="text-[10px] text-emerald-600">Book with confidence</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 5. ENTERPRISE-GRADE SECURITY (DARK BLUE BANNER) (Screenshot 2) */}
      <section className="bg-[#072654] text-white py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 space-y-6">
            <span className="text-blue-400 font-bold text-xs tracking-wider uppercase">ENTERPRISE INFRASTRUCTURE</span>
            <h2 className="text-3xl sm:text-5xl font-extrabold">Enterprise-Grade Security</h2>
            <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
              Your data and your bookings are protected by secure cloud infrastructure, data encryption, role-based access control and continuous monitoring.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
              <div className="bg-white/10 backdrop-blur-md border border-white/10 rounded-2xl p-5 space-y-1">
                <p className="font-bold text-sm">Secure Cloud Infrastructure</p>
                <p className="text-[11px] text-slate-300">Hardened enterprise-grade cloud servers.</p>
              </div>
              <div className="bg-white/10 backdrop-blur-md border border-white/10 rounded-2xl p-5 space-y-1">
                <p className="font-bold text-sm">Data Encryption</p>
                <p className="text-[11px] text-slate-300">All data encrypted in transit and at rest.</p>
              </div>
              <div className="bg-white/10 backdrop-blur-md border border-white/10 rounded-2xl p-5 space-y-1">
                <p className="font-bold text-sm">Role-Based Access Control</p>
                <p className="text-[11px] text-slate-300">Users only access authorised features.</p>
              </div>
              <div className="bg-white/10 backdrop-blur-md border border-white/10 rounded-2xl p-5 space-y-1">
                <p className="font-bold text-sm">Continuous Monitoring</p>
                <p className="text-[11px] text-slate-300">Infrastructure monitored around the clock.</p>
              </div>
            </div>
          </div>

          <div className="lg:col-span-5">
            {/* Security Overview Mockup */}
            <div className="bg-white/10 backdrop-blur-md border border-white/10 rounded-3xl p-6 space-y-4">
              <div className="flex justify-between items-center text-xs font-bold text-blue-300 border-b border-white/10 pb-3">
                <span>🛡️ Security Overview</span>
                <span>Protected</span>
              </div>
              <div className="space-y-2 text-xs">
                <div className="flex justify-between bg-white/5 p-3 rounded-xl"><span>DATA</span><span className="text-emerald-400 font-semibold">• Encrypted</span></div>
                <div className="flex justify-between bg-white/5 p-3 rounded-xl"><span>BOOKINGS</span><span className="text-emerald-400 font-semibold">• Secured</span></div>
                <div className="flex justify-between bg-white/5 p-3 rounded-xl"><span>USER ACCESS</span><span className="text-emerald-400 font-semibold">• Role-controlled</span></div>
                <div className="flex justify-between bg-white/5 p-3 rounded-xl"><span>INFRASTRUCTURE</span><span className="text-emerald-400 font-semibold">• Monitored</span></div>
              </div>
              <p className="text-[10px] text-emerald-400 text-center font-semibold pt-1">● All systems protected</p>
            </div>
          </div>
        </div>
      </section>

      {/* 6. BUILT FOR GROWTH & LOCAL SUPPORT (Screenshot 2 & 3) */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto w-full space-y-24">
        {/* Built for Growth */}
        <div>
          <div className="text-center max-w-3xl mx-auto mb-12">
            <span className="text-blue-600 font-bold text-xs tracking-wider uppercase block mb-1">SCALABILITY</span>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900">Built for Growth</h2>
            <p className="text-gray-500 text-xs sm:text-sm mt-2">Whether you're an individual booking a single desk or a business managing bookings across multiple cities, SPACIA scales with you.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-slate-50 border border-slate-200/80 rounded-3xl p-6 space-y-3">
              <span className="bg-blue-600 text-white text-[10px] px-3 py-1 rounded-full font-bold uppercase">INDIVIDUAL</span>
              <h4 className="font-bold text-sm text-gray-900">Single Workspace Booking</h4>
              <p className="text-xs text-gray-500">One booking at a time.</p>
              <div className="bg-white p-3 rounded-xl border border-gray-200 text-xs flex justify-between items-center">
                <span>Hot Desk — Level 2</span>
                <span className="bg-emerald-100 text-emerald-700 text-[10px] px-2 py-0.5 rounded-full font-bold">Confirmed</span>
              </div>
            </div>

            <div className="bg-[#072654] text-white rounded-3xl p-6 space-y-3">
              <span className="bg-blue-500 text-white text-[10px] px-3 py-1 rounded-full font-bold uppercase">BUSINESS</span>
              <h4 className="font-bold text-sm">Multi-City Booking Management</h4>
              <p className="text-xs text-blue-200">Manage across multiple locations.</p>
              <div className="space-y-1.5 text-xs">
                <div className="bg-white/10 p-2.5 rounded-xl flex justify-between"><span>Accra, Ghana</span><span className="bg-blue-600 px-2 py-0.5 rounded-full text-[10px]">4 booked</span></div>
                <div className="bg-white/10 p-2.5 rounded-xl flex justify-between"><span>Lagos, Nigeria</span><span className="bg-blue-600 px-2 py-0.5 rounded-full text-[10px]">2 booked</span></div>
                <div className="bg-white/10 p-2.5 rounded-xl flex justify-between"><span>Nairobi, Kenya</span><span className="bg-blue-600 px-2 py-0.5 rounded-full text-[10px]">6 booked</span></div>
              </div>
            </div>
          </div>
        </div>

        {/* Local Support with Global Standards */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 space-y-6">
            <span className="text-blue-600 font-bold text-xs tracking-wider uppercase">SUPPORT</span>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900">Local Support with Global Standards</h2>
            <p className="text-gray-600 text-sm sm:text-base leading-relaxed">
              Our users and partners benefit from responsive local support, backed by internationally recognised software engineering practices.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="bg-slate-50 p-4 rounded-2xl border border-slate-200/80 space-y-2">
                <h4 className="font-bold text-xs text-blue-600 uppercase">LOCAL SUPPORT</h4>
                <ul className="space-y-1 text-xs text-gray-600 font-medium">
                  <li>• Responsive local team</li>
                  <li>• Context-aware assistance</li>
                  <li>• Local knowledge</li>
                  <li>• User & partner support</li>
                </ul>
              </div>

              <div className="bg-slate-50 p-4 rounded-2xl border border-slate-200/80 space-y-2">
                <h4 className="font-bold text-xs text-blue-600 uppercase">GLOBAL STANDARDS</h4>
                <ul className="space-y-1 text-xs text-gray-600 font-medium">
                  <li>• Recognised engineering practices</li>
                  <li>• Enterprise-level processes</li>
                  <li>• Structured delivery</li>
                  <li>• Continuous improvement</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="lg:col-span-6">
            <img 
              src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=1000&q=80" 
              alt="Support Team High Five" 
              className="rounded-3xl shadow-xl w-full h-80 object-cover" 
            />
          </div>
        </div>
      </section>

      {/* 7. BUILT TO GIVE YOU CONFIDENCE (DARK BLUE SECTION) (Screenshot 3) */}
      <section className="bg-[#072654] text-white py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto space-y-12">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-extrabold">Built to Give You Confidence</h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {[
              { num: "01", title: "Built by theSOFTtribe", desc: "Founded in 1991 with 30+ years of software experience." },
              { num: "02", title: "Verified, Every Time", desc: "Every space, property, host and provider is verified." },
              { num: "03", title: "Enterprise-Grade Security", desc: "Secure infrastructure, encryption, and continuous monitoring." },
              { num: "04", title: "Built for Growth", desc: "From individual bookings to multi-city enterprises." },
              { num: "05", title: "Local Support & Global Standards", desc: "Responsive local support backed by global engineering." }
            ].map((card, idx) => (
              <div key={idx} className="bg-white/10 backdrop-blur-md border border-white/10 rounded-2xl p-5 space-y-2">
                <span className="text-blue-400 font-extrabold text-lg block">{card.num}</span>
                <h4 className="font-bold text-xs text-white">{card.title}</h4>
                <p className="text-[10px] text-slate-300 leading-relaxed">{card.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 8. TRUST THROUGH THE ENTIRE EXPERIENCE (Screenshot 3) */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto w-full">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900">Trust Through the Entire Experience</h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-5 gap-6 text-center">
          {[
            { step: "1", title: "DISCOVER", desc: "Find verified spaces across Work, Stay and Play." },
            { step: "2", title: "VERIFY", desc: "Every listing reviewed before it goes live." },
            { step: "3", title: "BOOK", desc: "Secure, confirmed booking in minutes." },
            { step: "4", title: "PAY", desc: "Protected payment on trusted infrastructure." },
            { step: "5", title: "MANAGE", desc: "Your bookings, always in one place." }
          ].map((item, idx) => (
            <div key={idx} className="bg-slate-50 border border-slate-200/80 rounded-2xl p-6 space-y-2 shadow-sm">
              <div className="w-10 h-10 rounded-full bg-blue-600 text-white font-bold text-xs flex items-center justify-center mx-auto mb-2">
                {item.step}
              </div>
              <h4 className="font-bold text-xs text-gray-900">{item.title}</h4>
              <p className="text-[10px] text-gray-500 leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
